// SafeHaven Scorecard - Main Component
import { CatListDropdown } from "@/components/FileInput";
import {
  percentileOptions0To100,
  percentileOptions5To95,
} from "@/utils/helper";
import Loader from "@/components/Loader";
import Chart5 from "../Chart5";
import AllocCEAplot from "../AllocCEAplot";
import RangeSelector from "./sections/RangeSelectror";
import useScorecard from "./hooks/useScorecard";
import BtnGroup from "../shared/BtnGroup";
import AnnualSPXReturns from "./sections/AnnualSPXReturns";
import FirstAsset from "./sections/FirstAsset";
import SecondAsset from "./sections/SecondAsset";
import CrossAsset from "./sections/CrossAsset";
const SingleAsset = ({
  multiAssetToggle,
  handleMultiAssetToggle,
  usePyScorecard = false,
}) => {

  const {
    // State
    loading,
    selectedType,
    fromYear,
    toYear,
    firstAsset,
    firstAssetName,
    firstAssetValues,
    secondAsset,
    secondAssetName,
    secondAssetValues,
    firstAssetDropdown,
    secondAssetDropdown,
    regimeCount,
    regimeSelectors,
    dataBar,
    regimeBar,
    regimeBarForNocrash,
    yearMatrixData,
    yearsList,
    alloc,
    lock,
    insuranceSliderValue,
    percentileDropdown: { percentile1Dropdown, percentile2Dropdown },
    isInsurance,
    showInsuranceSlider,
    allocCEAplotRef,

    // Arrays for charts
    minMaxAvgArray: { minArray, maxArray, avgArray },
    minMaxAvgArrayNoCrash: {
      averageArrayNocrash,
      maxArrayNocrash,
      minArrayNocrash,
    },
    minMaxAvgAssetArray: { minAssetArray, maxAssetArray, averageAssetArray },
    minMaxAvgAssetArrayNocrash: {
      minAssetArrayNocrash,
      maxAssetArrayNocrash,
      averageAssetArrayNocrash,
    },
    minMaxAvgAssetReshuffleArray: {
      minAssetReshuffleArray,
      maxAssetReshuffleArray,
      averageAssetReshuffleArray,
    },
    minMaxAvgMixNoCrash: { minMixNoCrash, maxMixNoCrash, avgMixNoCrash },
    mixAssetArray: { mixMinAssetArray, mixMaxAssetArray, mixAvgAssetArray },
    mixAssetReshuffleArray: {
      mixMinAssetReshuffleArray,
      mixMaxAssetReshuffleArray,
      mixAvgAssetReshuffleArray,
    },

    // Metrics
    amValues: { am, amNocrash, amMean, amMeanNocrash, amMeanReshuffle },
    gmValues: { gm, gmNocrash },
    mixAmGm: { mixAM, mixGM },
    mixAmGmReshuffle: { mixAMReshuffle, mixGMReshuffle },
    amGmMixNoCrash: { amMixNoCrash, gmMixNoCrash },

    // Matrix data for Chart5
    baseAssetMatrixRetruns,
    otherAssetMatrixRetruns,
    otherAssetMatrixReshuffleReturns,
    baseAssetMatrixReturnsNoCrash,
    otherAssetMatrixReturnsNoCrash,
    chart5ByMode,

    // Handlers
    handleYearChange,
    handleFirstAsset,
    handleSecondAsset,
    handleDefault,
    handleReshuffle,
    handleNocrash,
    handleAllocSliderChange,
    handleAllocCommit,
    handleLock,
    handleInsuranceSliderValue,
    handlePercentileDropdown,
    updateRegime,
    handleRegimeCommit,
    setRegimeCount,
    setPercentile2DataMaxAllocation,
  } = useScorecard({ usePyScorecard }); // SCORECARD_PARITY_TESTKIT
  const firstAssetArray = {
    normal: {
      min: minArray,
      max: maxArray,
      average: avgArray,
      am,
      gm,
    },
    reshuffle: {
      min: minArray,
      max: maxArray,
      average: avgArray,
      am,
      gm,
    },
    nocrash: {
      min: minArrayNocrash,
      max: maxArrayNocrash,
      average: averageArrayNocrash,
      am: amNocrash,
      gm: gmNocrash,
    },
  };
  const secondAssetArray = {
    normal: {
      min: minAssetArray,
      max: maxAssetArray,
      average: averageAssetArray,
      am: amMean,
    },
    reshuffle: {
      min: minAssetReshuffleArray,
      max: maxAssetReshuffleArray,
      average: averageAssetReshuffleArray,
      am: amMeanReshuffle,
    },
    nocrash: {
      min: minAssetArrayNocrash,
      max: maxAssetArrayNocrash,
      average: averageAssetArrayNocrash,
      am: amMeanNocrash,
    },
  };
  const mixAssetArray = {
    normal: {
      min: mixMinAssetArray,
      max: mixMaxAssetArray,
      average: mixAvgAssetArray,
      am: mixAM,
      gm: mixGM,
      diffAM: mixAM - am,
      diffGM: mixGM - gm,
    },
    reshuffle: {
      min: mixMinAssetReshuffleArray,
      max: mixMaxAssetReshuffleArray,
      average: mixAvgAssetReshuffleArray,
      am: mixAMReshuffle,
      gm: mixGMReshuffle,
      diffAM: mixAMReshuffle - am,
      diffGM: mixGMReshuffle - gm,
    },
    nocrash: {
      min: minMixNoCrash,
      max: maxMixNoCrash,
      average: avgMixNoCrash,
      am: amMixNoCrash,
      gm: gmMixNoCrash,
      diffAM: amMixNoCrash - amNocrash,
      diffGM: gmMixNoCrash - gmNocrash,
    },
  };
  return (
    <div className="w-full mx-auto max-w-7xl py-5 text-sm text-gray-700 px-4">
      <RangeSelector
        handleYearChange={handleYearChange}
        regimeCount={regimeCount}
        setRegimeCount={setRegimeCount}
        regimeSelectors={regimeSelectors}
        dataBar={dataBar}
        updateRegime={updateRegime}
        onRegimeCommit={handleRegimeCommit}
        multiAssetToggle={multiAssetToggle}
        handleMultiAssetToggle={handleMultiAssetToggle}
      />

      <div className="flex justify-center max-w-2xl mx-auto px-4 text-left mb-5">
        <BtnGroup
          buttons={[
            { value: "normal", label: "Default", onClick: handleDefault },
            {
              value: "reshuffle",
              label: "Reshuffle",
              onClick: handleReshuffle,
            },
            { value: "nocrash", label: "No Crash", onClick: handleNocrash },
          ]}
          selectedValue={selectedType}
          testIdPrefix="main-single-type"
        />
      </div>

      <div className="relative min-h-[320px]">
        <Loader loading={loading} contained />

        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6 xl:gap-10 justify-center w-full mb-0 lg:mb-10 ${
            loading ? "pointer-events-none" : ""
          }`}
          aria-busy={loading}
        >
          <div className="w-full">
              <AnnualSPXReturns
                selectedType={selectedType}
                regimeBar={regimeBar}
                regimeBarForNocrash={regimeBarForNocrash}
                firstAsset={firstAsset}
                firstAssetName={firstAssetName}
                firstAssetDropdown={firstAssetDropdown}
                handleFirstAsset={handleFirstAsset}
              />
            <div className="grid grid-cols-12 gap-5 my-2 items-center">
              <div className="col-span-9">
                <hr className="border-1 border-black" />
              </div>
              <div className="col-span-3">
                <p>Bootstrap</p>
              </div>
            </div>

            <div>
                <FirstAsset
                  selectedType={selectedType}
                  assetArray={firstAssetArray[selectedType]}
                  firstAssetName={firstAssetName}
                />
                <SecondAsset
                  assetArray={secondAssetArray[selectedType]}
                  secondAsset={secondAsset}
                  secondAssetOptions={secondAssetDropdown}
                  handleSecondAsset={handleSecondAsset}
                />
              <div className="grid grid-cols-12 my-4 sm:my-3">
                <div className="col-span-12 mb-2">
                  <hr className="border-1 border-black" />
                </div>
                <div className="col-span-12">
                  <hr className="border-1 border-black" />
                </div>
              </div>
                <CrossAsset
                  assetArray={mixAssetArray[selectedType]}
                  firstAssetName={firstAssetName}
                  secondAssetName={secondAssetName}
                  alloc={alloc}
                  handleInsuranceSliderValue={handleInsuranceSliderValue}
                  handleAllocSliderChange={handleAllocSliderChange}
                  handleAllocCommit={handleAllocCommit}
                  handleLock={handleLock}
                  lock={lock}
                  insuranceSliderValue={insuranceSliderValue}
                  showInsuranceSlider={showInsuranceSlider}
                />
            </div>
          </div>

          <div className="w-full">
            <div>
              <div className="mb-4 relative">
                <div className="w-full chart-wrap">
                  <div className="w-full bg-white rounded-lg shadow-xl shadow-slate-300 p-5">
                    <Chart5
                      assetType={isInsurance}
                      fromYear={fromYear}
                      toYear={toYear}
                      firstAssetValues={firstAssetValues}
                      secondAssetValues={secondAssetValues}
                      yearsMatrix={yearMatrixData}
                      yearsList={yearsList}
                      percentile1Dropdown={percentile1Dropdown}
                      percentile2Dropdown={percentile2Dropdown}
                      baseAssetMatrixRetruns={baseAssetMatrixRetruns}
                      otherAssetMatrixRetruns={otherAssetMatrixRetruns}
                      reshuffle={selectedType === "reshuffle"}
                      normal={selectedType === "normal"}
                      otherAssetMatrixReshuffleReturns={
                        otherAssetMatrixReshuffleReturns
                      }
                      nocrash={selectedType === "nocrash"}
                      baseAssetMatrixReturnsNoCrash={
                        baseAssetMatrixReturnsNoCrash
                      }
                      otherAssetMatrixReturnsNoCrash={
                        otherAssetMatrixReturnsNoCrash
                      }
                      setPercentile2DataMaxAllocation={
                        setPercentile2DataMaxAllocation
                      }
                      precomputedPercentile1={
                        usePyScorecard
                          ? chart5ByMode?.[selectedType]?.percentile1
                          : null
                      }
                      precomputedPercentile2={
                        usePyScorecard
                          ? chart5ByMode?.[selectedType]?.percentile2
                          : null
                      }
                    />
                    <div className="flex justify-center gap-5 text-sm mt-4">
                      <div className="">
                        <CatListDropdown
                          id={"percentile_1"}
                          value={percentile1Dropdown}
                          options={percentileOptions0To100}
                          onChange={(event) =>
                            handlePercentileDropdown(event, "percentile_1")
                          }
                          data-testid="main-single-percentile-1-select"
                        />
                      </div>
                      <div className="">
                        <CatListDropdown
                          id={"percentile_2"}
                          value={percentile2Dropdown}
                          options={percentileOptions5To95}
                          onChange={(event) =>
                            handlePercentileDropdown(event, "percentile_2")
                          }
                          data-testid="main-single-percentile-2-select"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full mt-6 lg:mt-20">
                <div className="chart-wrap">
                  <div className="flex flex-col mx-auto w-full bg-white rounded-lg shadow-xl shadow-slate-300 p-5">
                    {selectedType === "normal" && (
                      <AllocCEAplot
                        ref={allocCEAplotRef}
                        isMulti
                        coordinates={[
                          {
                            xAxis: (-(mixAM - am)).toFixed(1),
                            yAxis: (-(mixGM - gm)).toFixed(1),
                            borderColor: "rgba(141, 130, 205, 0.6)",
                          },
                        ]}
                        xAxis={(-(mixAM - am)).toFixed(1)}
                        yAxis={(-(mixGM - gm)).toFixed(1)}
                      />
                    )}
                    {selectedType === "reshuffle" && (
                      <AllocCEAplot
                        ref={allocCEAplotRef}
                        isMulti
                        coordinates={[
                          {
                            xAxis: (-(mixAMReshuffle - am)).toFixed(1),
                            yAxis: (-(mixGMReshuffle - gm)).toFixed(1),
                            borderColor: "rgba(141, 130, 205, 0.6)",
                          },
                        ]}
                        xAxis={(-(mixAMReshuffle - am)).toFixed(1)}
                        yAxis={(-(mixGMReshuffle - gm)).toFixed(1)}
                      />
                    )}
                    {selectedType === "nocrash" && (
                      <AllocCEAplot
                        ref={allocCEAplotRef}
                        isMulti
                        coordinates={[
                          {
                            xAxis: (-(amMixNoCrash - amNocrash)).toFixed(1),
                            yAxis: (-(gmMixNoCrash - gmNocrash)).toFixed(1),
                            borderColor: "rgba(141, 130, 205, 0.6)",
                          },
                        ]}
                        xAxis={(-(amMixNoCrash - amNocrash)).toFixed(1)}
                        yAxis={(-(gmMixNoCrash - gmNocrash)).toFixed(1)}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleAsset;
